import React, { useState, useRef, useMemo, useCallback } from 'react';
import * as XLSX from 'xlsx';
import CryptoJS from 'crypto-js';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { toast } from 'sonner';
import { 
  Upload, FileSpreadsheet, ChevronDown, ChevronRight, Download,
  UserPlus, UserMinus, AlertTriangle, CreditCard, RefreshCw,
  CheckCircle, XCircle, Trash2, Eye, EyeOff, Lock
} from 'lucide-react';

// Expected columns in the bank file
const EXPECTED_COLUMNS = [
  'PYMT_PROD_TYPE_CODE', 'PYMT_MODE', 'DEBIT_ACC_NO', 'BNF_NAME', 'BENE_ACC_NO',
  'BENE_IFSC', 'AMOUNT', 'DEBIT_NARR', 'CREDIT_NARR', 'MOBILE_NUM', 'EMAIL_ID',
  'REMARK', 'PYMT_DATE', 'REF_NO', 'ADDL_INFO1', 'ADDL_INFO2', 'ADDL_INFO3',
  'ADDL_INFO4', 'ADDL_INFO5'
];

// Encryption key (generated per session)
const generateEncryptionKey = () => {
  return CryptoJS.lib.WordArray.random(32).toString();
};

// Encrypt data
const encryptData = (data, key) => {
  return CryptoJS.AES.encrypt(JSON.stringify(data), key).toString();
};

// Decrypt data
const decryptData = (encryptedData, key) => {
  try {
    const bytes = CryptoJS.AES.decrypt(encryptedData, key);
    return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
  } catch {
    return null;
  }
};

const BankFileComparison = () => {
  // Session encryption key
  const [encryptionKey] = useState(() => generateEncryptionKey());
  
  // File states (encrypted)
  const [lastMonthData, setLastMonthData] = useState(null);
  const [currentMonthData, setCurrentMonthData] = useState(null);
  const [lastMonthFileName, setLastMonthFileName] = useState('');
  const [currentMonthFileName, setCurrentMonthFileName] = useState('');
  
  // UI states
  const [loading, setLoading] = useState(false);
  const [comparisonResults, setComparisonResults] = useState(null);
  const [showSensitiveData, setShowSensitiveData] = useState(false);
  
  // Collapsible states
  const [openSections, setOpenSections] = useState({
    newEmployees: true,
    missingEmployees: true,
    salaryChanges: true,
    accountChanges: true
  });

  // File input refs
  const lastMonthInputRef = useRef(null);
  const currentMonthInputRef = useRef(null);

  // Validate columns (case-insensitive)
  const validateColumns = (headers) => {
    const normalizedHeaders = headers.map(h => h?.toString().toUpperCase().trim());
    const normalizedExpected = EXPECTED_COLUMNS.map(c => c.toUpperCase());
    
    const missingColumns = [];
    const extraColumns = [];
    
    normalizedExpected.forEach((col, index) => {
      if (normalizedHeaders[index] !== col) {
        if (!normalizedHeaders.includes(col)) {
          missingColumns.push(EXPECTED_COLUMNS[index]);
        }
      }
    });
    
    normalizedHeaders.forEach(header => {
      if (header && !normalizedExpected.includes(header)) {
        extraColumns.push(header);
      }
    });

    // Check column order
    let orderCorrect = true;
    for (let i = 0; i < normalizedExpected.length; i++) {
      if (normalizedHeaders[i] !== normalizedExpected[i]) {
        orderCorrect = false;
        break;
      }
    }

    return {
      isValid: missingColumns.length === 0,
      orderCorrect,
      missingColumns,
      extraColumns
    };
  };

  // Parse Excel file
  const parseExcelFile = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          
          // Get headers from first row
          const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
          const headers = [];
          for (let col = range.s.c; col <= range.e.c; col++) {
            const cell = worksheet[XLSX.utils.encode_cell({ r: 0, c: col })];
            headers.push(cell ? cell.v : '');
          }
          
          // Validate columns
          const validation = validateColumns(headers);
          if (!validation.isValid) {
            reject({
              type: 'validation',
              message: `Missing columns: ${validation.missingColumns.join(', ')}`,
              details: validation
            });
            return;
          }

          if (!validation.orderCorrect) {
            reject({
              type: 'order',
              message: 'Column order does not match expected format',
              details: validation
            });
            return;
          }

          // Convert to JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
          
          // Normalize column names to uppercase
          const normalizedData = jsonData.map(row => {
            const normalizedRow = {};
            Object.keys(row).forEach(key => {
              normalizedRow[key.toUpperCase().trim()] = row[key];
            });
            return normalizedRow;
          });

          resolve(normalizedData);
        } catch (error) {
          reject({ type: 'parse', message: 'Failed to parse Excel file', error });
        }
      };
      
      reader.onerror = () => reject({ type: 'read', message: 'Failed to read file' });
      reader.readAsArrayBuffer(file);
    });
  };

  // Handle file upload
  const handleFileUpload = async (file, type) => {
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      toast.error('Invalid file format', {
        description: 'Only .xlsx files are supported'
      });
      return;
    }

    setLoading(true);
    
    try {
      const data = await parseExcelFile(file);
      
      // Encrypt and store
      const encryptedData = encryptData(data, encryptionKey);
      
      if (type === 'lastMonth') {
        setLastMonthData(encryptedData);
        setLastMonthFileName(file.name);
        toast.success('Last month file uploaded', {
          description: `${data.length} records loaded`
        });
      } else {
        setCurrentMonthData(encryptedData);
        setCurrentMonthFileName(file.name);
        toast.success('Current month file uploaded', {
          description: `${data.length} records loaded`
        });
      }
      
      // Clear comparison results when new file is uploaded
      setComparisonResults(null);
      
    } catch (error) {
      console.error('File upload error:', error);
      if (error.type === 'validation') {
        toast.error('Column validation failed', {
          description: error.message
        });
      } else if (error.type === 'order') {
        toast.error('Column order mismatch', {
          description: 'Please ensure columns are in the correct order'
        });
      } else {
        toast.error('File upload failed', {
          description: error.message || 'Unknown error'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Compare files
  const compareFiles = useCallback(() => {
    if (!lastMonthData || !currentMonthData) {
      toast.error('Please upload both files first');
      return;
    }

    setLoading(true);

    try {
      const lastMonth = decryptData(lastMonthData, encryptionKey);
      const currentMonth = decryptData(currentMonthData, encryptionKey);

      if (!lastMonth || !currentMonth) {
        toast.error('Failed to decrypt data');
        return;
      }

      // Create maps for comparison using BNF_NAME + BENE_ACC_NO as composite key
      const lastMonthMap = new Map();
      lastMonth.forEach(record => {
        const key = `${(record.BNF_NAME || '').toString().toUpperCase().trim()}|${(record.BENE_ACC_NO || '').toString().trim()}`;
        lastMonthMap.set(key, record);
      });

      const currentMonthMap = new Map();
      currentMonth.forEach(record => {
        const key = `${(record.BNF_NAME || '').toString().toUpperCase().trim()}|${(record.BENE_ACC_NO || '').toString().trim()}`;
        currentMonthMap.set(key, record);
      });

      // Also create maps by name only for account change detection
      const lastMonthByName = new Map();
      lastMonth.forEach(record => {
        const name = (record.BNF_NAME || '').toString().toUpperCase().trim();
        if (!lastMonthByName.has(name)) {
          lastMonthByName.set(name, []);
        }
        lastMonthByName.get(name).push(record);
      });

      const currentMonthByName = new Map();
      currentMonth.forEach(record => {
        const name = (record.BNF_NAME || '').toString().toUpperCase().trim();
        if (!currentMonthByName.has(name)) {
          currentMonthByName.set(name, []);
        }
        currentMonthByName.get(name).push(record);
      });

      // 1. Find new employees (in current but not in last by name)
      const newEmployees = [];
      currentMonthByName.forEach((records, name) => {
        if (!lastMonthByName.has(name)) {
          records.forEach(record => newEmployees.push(record));
        }
      });

      // 2. Find missing employees (in last but not in current by name)
      const missingEmployees = [];
      lastMonthByName.forEach((records, name) => {
        if (!currentMonthByName.has(name)) {
          records.forEach(record => missingEmployees.push(record));
        }
      });

      // 3. Find salary changes (same name + account, different amount)
      const salaryChanges = [];
      currentMonthMap.forEach((currentRecord, key) => {
        const lastRecord = lastMonthMap.get(key);
        if (lastRecord) {
          const currentAmount = parseFloat(currentRecord.AMOUNT) || 0;
          const lastAmount = parseFloat(lastRecord.AMOUNT) || 0;
          if (currentAmount !== lastAmount) {
            salaryChanges.push({
              current: currentRecord,
              previous: lastRecord,
              difference: currentAmount - lastAmount,
              percentChange: lastAmount !== 0 ? ((currentAmount - lastAmount) / lastAmount * 100).toFixed(2) : 'N/A'
            });
          }
        }
      });

      // 4. Find account number changes (same name, different account)
      const accountChanges = [];
      currentMonthByName.forEach((currentRecords, name) => {
        const lastRecords = lastMonthByName.get(name);
        if (lastRecords) {
          currentRecords.forEach(currentRecord => {
            const currentAccount = (currentRecord.BENE_ACC_NO || '').toString().trim();
            const hasMatchingAccount = lastRecords.some(lr => 
              (lr.BENE_ACC_NO || '').toString().trim() === currentAccount
            );
            
            if (!hasMatchingAccount && lastRecords.length > 0) {
              // Account changed - find the old account
              const oldRecord = lastRecords[0]; // Take first matching record
              accountChanges.push({
                name: currentRecord.BNF_NAME,
                oldAccount: oldRecord.BENE_ACC_NO,
                newAccount: currentRecord.BENE_ACC_NO,
                oldIFSC: oldRecord.BENE_IFSC,
                newIFSC: currentRecord.BENE_IFSC,
                current: currentRecord,
                previous: oldRecord
              });
            }
          });
        }
      });

      setComparisonResults({
        newEmployees,
        missingEmployees,
        salaryChanges,
        accountChanges,
        summary: {
          lastMonthCount: lastMonth.length,
          currentMonthCount: currentMonth.length,
          newCount: newEmployees.length,
          missingCount: missingEmployees.length,
          salaryChangesCount: salaryChanges.length,
          accountChangesCount: accountChanges.length
        }
      });

      toast.success('Comparison completed', {
        description: `Found ${newEmployees.length} new, ${missingEmployees.length} missing, ${salaryChanges.length} salary changes, ${accountChanges.length} account changes`
      });

    } catch (error) {
      console.error('Comparison error:', error);
      toast.error('Comparison failed', {
        description: error.message || 'Unknown error'
      });
    } finally {
      setLoading(false);
    }
  }, [lastMonthData, currentMonthData, encryptionKey]);

  // Clear all data
  const clearAllData = () => {
    setLastMonthData(null);
    setCurrentMonthData(null);
    setLastMonthFileName('');
    setCurrentMonthFileName('');
    setComparisonResults(null);
    if (lastMonthInputRef.current) lastMonthInputRef.current.value = '';
    if (currentMonthInputRef.current) currentMonthInputRef.current.value = '';
    toast.success('All data cleared');
  };

  // Export to Excel
  const exportToExcel = () => {
    if (!comparisonResults) {
      toast.error('No comparison results to export');
      return;
    }

    const workbook = XLSX.utils.book_new();

    // Summary sheet
    const summaryData = [
      ['Bank File Comparison Report'],
      ['Generated on', new Date().toLocaleString()],
      [''],
      ['Summary'],
      ['Last Month Records', comparisonResults.summary.lastMonthCount],
      ['Current Month Records', comparisonResults.summary.currentMonthCount],
      ['New Employees', comparisonResults.summary.newCount],
      ['Missing Employees', comparisonResults.summary.missingCount],
      ['Salary Changes', comparisonResults.summary.salaryChangesCount],
      ['Account Changes', comparisonResults.summary.accountChangesCount]
    ];
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

    // New Employees sheet
    if (comparisonResults.newEmployees.length > 0) {
      const newEmpSheet = XLSX.utils.json_to_sheet(comparisonResults.newEmployees);
      XLSX.utils.book_append_sheet(workbook, newEmpSheet, 'New Employees');
    }

    // Missing Employees sheet
    if (comparisonResults.missingEmployees.length > 0) {
      const missingSheet = XLSX.utils.json_to_sheet(comparisonResults.missingEmployees);
      XLSX.utils.book_append_sheet(workbook, missingSheet, 'Missing Employees');
    }

    // Salary Changes sheet
    if (comparisonResults.salaryChanges.length > 0) {
      const salaryData = comparisonResults.salaryChanges.map(item => ({
        'Employee Name': item.current.BNF_NAME,
        'Account Number': item.current.BENE_ACC_NO,
        'Previous Amount': item.previous.AMOUNT,
        'Current Amount': item.current.AMOUNT,
        'Difference': item.difference,
        'Change %': item.percentChange
      }));
      const salarySheet = XLSX.utils.json_to_sheet(salaryData);
      XLSX.utils.book_append_sheet(workbook, salarySheet, 'Salary Changes');
    }

    // Account Changes sheet
    if (comparisonResults.accountChanges.length > 0) {
      const accountData = comparisonResults.accountChanges.map(item => ({
        'Employee Name': item.name,
        'Old Account': item.oldAccount,
        'New Account': item.newAccount,
        'Old IFSC': item.oldIFSC,
        'New IFSC': item.newIFSC
      }));
      const accountSheet = XLSX.utils.json_to_sheet(accountData);
      XLSX.utils.book_append_sheet(workbook, accountSheet, 'Account Changes');
    }

    // Download
    const fileName = `Bank_Comparison_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
    toast.success('Report exported', { description: fileName });
  };

  // Mask sensitive data
  const maskData = (value, showFull = false) => {
    if (showFull || showSensitiveData) return value;
    if (!value) return '-';
    const str = value.toString();
    if (str.length <= 4) return '****';
    return str.substring(0, 2) + '****' + str.substring(str.length - 2);
  };

  // Format currency
  const formatCurrency = (value) => {
    const num = parseFloat(value) || 0;
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(num);
  };

  // Toggle section
  const toggleSection = (section) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Bank File Comparison</h2>
          <p className="text-slate-600">Compare last month and current month bank files</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowSensitiveData(!showSensitiveData)}
            className="gap-2"
          >
            {showSensitiveData ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            {showSensitiveData ? 'Hide' : 'Show'} Sensitive Data
          </Button>
          <Button
            variant="outline"
            onClick={clearAllData}
            className="gap-2 text-red-600 hover:text-red-700"
          >
            <Trash2 className="w-4 h-4" />
            Clear All
          </Button>
        </div>
      </div>

      {/* File Upload Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Last Month File */}
        <Card className={lastMonthData ? 'border-green-300 bg-green-50' : ''}>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Last Month Bank File
            </CardTitle>
            <CardDescription>
              Upload the previous month's bank file for comparison
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <input
                type="file"
                ref={lastMonthInputRef}
                accept=".xlsx"
                onChange={(e) => handleFileUpload(e.target.files?.[0], 'lastMonth')}
                className="hidden"
              />
              <Button
                onClick={() => lastMonthInputRef.current?.click()}
                disabled={loading}
                variant={lastMonthData ? 'outline' : 'default'}
                className="w-full gap-2"
              >
                <Upload className="w-4 h-4" />
                {lastMonthData ? 'Replace File' : 'Upload File'}
              </Button>
              {lastMonthFileName && (
                <div className="flex items-center gap-2 text-sm text-green-700">
                  <CheckCircle className="w-4 h-4" />
                  {lastMonthFileName}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Current Month File */}
        <Card className={currentMonthData ? 'border-green-300 bg-green-50' : ''}>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Current Month Bank File
            </CardTitle>
            <CardDescription>
              Upload the current month's bank file for comparison
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <input
                type="file"
                ref={currentMonthInputRef}
                accept=".xlsx"
                onChange={(e) => handleFileUpload(e.target.files?.[0], 'currentMonth')}
                className="hidden"
              />
              <Button
                onClick={() => currentMonthInputRef.current?.click()}
                disabled={loading}
                variant={currentMonthData ? 'outline' : 'default'}
                className="w-full gap-2"
              >
                <Upload className="w-4 h-4" />
                {currentMonthData ? 'Replace File' : 'Upload File'}
              </Button>
              {currentMonthFileName && (
                <div className="flex items-center gap-2 text-sm text-green-700">
                  <CheckCircle className="w-4 h-4" />
                  {currentMonthFileName}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Compare Button */}
      <div className="flex justify-center gap-4">
        <Button
          onClick={compareFiles}
          disabled={loading || !lastMonthData || !currentMonthData}
          size="lg"
          className="gap-2 bg-emerald-600 hover:bg-emerald-700"
        >
          {loading ? (
            <RefreshCw className="w-5 h-5 animate-spin" />
          ) : (
            <FileSpreadsheet className="w-5 h-5" />
          )}
          Compare Files
        </Button>
        {comparisonResults && (
          <Button
            onClick={exportToExcel}
            variant="outline"
            size="lg"
            className="gap-2"
          >
            <Download className="w-5 h-5" />
            Export Report
          </Button>
        )}
      </div>

      {/* Results Section */}
      {comparisonResults && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <UserPlus className="w-8 h-8 text-blue-600" />
                  <div>
                    <p className="text-2xl font-bold text-blue-700">{comparisonResults.summary.newCount}</p>
                    <p className="text-sm text-blue-600">New Employees</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-orange-50 border-orange-200">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <UserMinus className="w-8 h-8 text-orange-600" />
                  <div>
                    <p className="text-2xl font-bold text-orange-700">{comparisonResults.summary.missingCount}</p>
                    <p className="text-sm text-orange-600">Missing Employees</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-8 h-8 text-purple-600" />
                  <div>
                    <p className="text-2xl font-bold text-purple-700">{comparisonResults.summary.salaryChangesCount}</p>
                    <p className="text-sm text-purple-600">Salary Changes</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-red-50 border-red-200">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <CreditCard className="w-8 h-8 text-red-600" />
                  <div>
                    <p className="text-2xl font-bold text-red-700">{comparisonResults.summary.accountChangesCount}</p>
                    <p className="text-sm text-red-600">Account Changes</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* New Employees */}
          <Collapsible open={openSections.newEmployees} onOpenChange={() => toggleSection('newEmployees')}>
            <Card>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-slate-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <UserPlus className="w-5 h-5 text-blue-600" />
                      <CardTitle>New Employees</CardTitle>
                      <Badge className="bg-blue-100 text-blue-700">{comparisonResults.newEmployees.length}</Badge>
                    </div>
                    {openSections.newEmployees ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                  </div>
                  <CardDescription>Employees present in current month but not in last month</CardDescription>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent>
                  {comparisonResults.newEmployees.length === 0 ? (
                    <p className="text-slate-500 text-center py-4">No new employees found</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>#</TableHead>
                            <TableHead>Employee Name</TableHead>
                            <TableHead>Account Number</TableHead>
                            <TableHead>IFSC</TableHead>
                            <TableHead>Amount</TableHead>
                            <TableHead>Mobile</TableHead>
                            <TableHead>Email</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {comparisonResults.newEmployees.map((emp, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{idx + 1}</TableCell>
                              <TableCell className="font-medium">{emp.BNF_NAME}</TableCell>
                              <TableCell className="font-mono">{maskData(emp.BENE_ACC_NO)}</TableCell>
                              <TableCell>{emp.BENE_IFSC}</TableCell>
                              <TableCell>{formatCurrency(emp.AMOUNT)}</TableCell>
                              <TableCell>{maskData(emp.MOBILE_NUM)}</TableCell>
                              <TableCell>{maskData(emp.EMAIL_ID)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Missing Employees */}
          <Collapsible open={openSections.missingEmployees} onOpenChange={() => toggleSection('missingEmployees')}>
            <Card>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-slate-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <UserMinus className="w-5 h-5 text-orange-600" />
                      <CardTitle>Missing Employees</CardTitle>
                      <Badge className="bg-orange-100 text-orange-700">{comparisonResults.missingEmployees.length}</Badge>
                    </div>
                    {openSections.missingEmployees ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                  </div>
                  <CardDescription>Employees present in last month but not in current month</CardDescription>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent>
                  {comparisonResults.missingEmployees.length === 0 ? (
                    <p className="text-slate-500 text-center py-4">No missing employees found</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>#</TableHead>
                            <TableHead>Employee Name</TableHead>
                            <TableHead>Account Number</TableHead>
                            <TableHead>IFSC</TableHead>
                            <TableHead>Last Amount</TableHead>
                            <TableHead>Mobile</TableHead>
                            <TableHead>Email</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {comparisonResults.missingEmployees.map((emp, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{idx + 1}</TableCell>
                              <TableCell className="font-medium">{emp.BNF_NAME}</TableCell>
                              <TableCell className="font-mono">{maskData(emp.BENE_ACC_NO)}</TableCell>
                              <TableCell>{emp.BENE_IFSC}</TableCell>
                              <TableCell>{formatCurrency(emp.AMOUNT)}</TableCell>
                              <TableCell>{maskData(emp.MOBILE_NUM)}</TableCell>
                              <TableCell>{maskData(emp.EMAIL_ID)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Salary Changes */}
          <Collapsible open={openSections.salaryChanges} onOpenChange={() => toggleSection('salaryChanges')}>
            <Card>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-slate-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <AlertTriangle className="w-5 h-5 text-purple-600" />
                      <CardTitle>Salary Changes</CardTitle>
                      <Badge className="bg-purple-100 text-purple-700">{comparisonResults.salaryChanges.length}</Badge>
                    </div>
                    {openSections.salaryChanges ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                  </div>
                  <CardDescription>Employees with different salary amounts between months</CardDescription>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent>
                  {comparisonResults.salaryChanges.length === 0 ? (
                    <p className="text-slate-500 text-center py-4">No salary changes found</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>#</TableHead>
                            <TableHead>Employee Name</TableHead>
                            <TableHead>Account Number</TableHead>
                            <TableHead>Previous Amount</TableHead>
                            <TableHead>Current Amount</TableHead>
                            <TableHead>Difference</TableHead>
                            <TableHead>Change %</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {comparisonResults.salaryChanges.map((item, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{idx + 1}</TableCell>
                              <TableCell className="font-medium">{item.current.BNF_NAME}</TableCell>
                              <TableCell className="font-mono">{maskData(item.current.BENE_ACC_NO)}</TableCell>
                              <TableCell>{formatCurrency(item.previous.AMOUNT)}</TableCell>
                              <TableCell>{formatCurrency(item.current.AMOUNT)}</TableCell>
                              <TableCell className={item.difference > 0 ? 'text-green-600' : 'text-red-600'}>
                                {item.difference > 0 ? '+' : ''}{formatCurrency(item.difference)}
                              </TableCell>
                              <TableCell>
                                <Badge className={item.difference > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                                  {item.difference > 0 ? '+' : ''}{item.percentChange}%
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Account Changes */}
          <Collapsible open={openSections.accountChanges} onOpenChange={() => toggleSection('accountChanges')}>
            <Card>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-slate-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CreditCard className="w-5 h-5 text-red-600" />
                      <CardTitle>Account Number Changes</CardTitle>
                      <Badge className="bg-red-100 text-red-700">{comparisonResults.accountChanges.length}</Badge>
                    </div>
                    {openSections.accountChanges ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                  </div>
                  <CardDescription>Employees whose bank account number has changed</CardDescription>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent>
                  {comparisonResults.accountChanges.length === 0 ? (
                    <p className="text-slate-500 text-center py-4">No account changes found</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>#</TableHead>
                            <TableHead>Employee Name</TableHead>
                            <TableHead>Old Account</TableHead>
                            <TableHead>New Account</TableHead>
                            <TableHead>Old IFSC</TableHead>
                            <TableHead>New IFSC</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {comparisonResults.accountChanges.map((item, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{idx + 1}</TableCell>
                              <TableCell className="font-medium">{item.name}</TableCell>
                              <TableCell className="font-mono text-red-600">{maskData(item.oldAccount)}</TableCell>
                              <TableCell className="font-mono text-green-600">{maskData(item.newAccount)}</TableCell>
                              <TableCell className="text-red-600">{item.oldIFSC}</TableCell>
                              <TableCell className="text-green-600">{item.newIFSC}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        </div>
      )}
    </div>
  );
};

export default BankFileComparison;
